import React, {useState, useEffect} from 'react';
import {withTranslation} from 'react-i18next';
import {ErrorMessage, Field} from 'formik';
import styled from 'styled-components';
import 'react-datepicker/dist/react-datepicker.css';

import {FormInput} from '../../../shared/form/form-control/FormInput.js';
import {Icon} from '../../../shared/elements/icon/Icon.js';
import {Col} from '../../../shared/grid/grid-features/Col.js';
import {Row} from '../../../shared/grid/grid-features/Row.js';
import {Accordion} from '../../../shared/elements/accordion/Accordion.js';
import {FormDatePicker} from '../../../shared/form/form-control/FormDatePicker.js';
import {FormLabel} from '../../../shared/form/form-features/FormLabel.js';
import {getArticleGroupsTypes} from './endpoints.js';
import errorHandler from '../../../util/errorHandler.js';
import {ValidationErrorTxt} from '../../../shared/form/form-features/ValidationErrorTxt.js';
import {getDateOfSameDayNextWeek, getDateToday} from './helper.js';

function AddArticleGroup({t, path, onDelete, setFieldValue, setFieldTouched, articleGroup, showErrors}) {
    const [opened, setOpened] = useState(false);
    const [startDate, setStartDate] = useState(
        articleGroup.startDate ? new Date(articleGroup.startDate) : getDateToday(),
    );
    const [endDate, setEndDate] = useState(
        articleGroup.endDate ? new Date(articleGroup.endDate) : getDateOfSameDayNextWeek(startDate),
    );

    const [articleGroupTypes, setArticleGroupTypes] = useState([]);

    useEffect(() => {
        getArticleGroupsTypes()
            .then((res) => setArticleGroupTypes(res.data))
            .catch((res) => errorHandler(res.data));
    }, []);

    const togglePanel = () => {
        setOpened(!opened);
    };

    useEffect(() => {
        setFieldValue(`${path}.startDate`, startDate);
        setFieldValue(`${path}.endDate`, endDate);
        setFieldTouched(`${path.endDate}`, true);
    }, [startDate, endDate]);

    if (articleGroupTypes.length === 0) return <p>{t('general.Loading')}</p>;

    return (
        <Accordion
            link
            // We only want to show the errors are there when the field is collapsed
            showErrors={showErrors && !opened}
            color="primary"
            onClick={() => togglePanel()}
            onDelete={() => onDelete()}
            isDisabled={articleGroup.hasLinkedStockData}
            accordionHeader={
                <>
                    {articleGroup.name || t('customer.OneArticleGroup')}
                    {opened ? <Icon icon="KeyboardArrowUp" /> : <Icon icon="KeyboardArrowDown" />}
                </>
            }
        >
            {opened && (
                <Accordion.Body>
                    <Wrapper>
                        <Col colsize="50">
                            <FormLabel>{t('customer.ArticleGroup.Name')}</FormLabel>
                            <FormInput name={`${path}.name`} placeholder={t('customer.ArticleGroup.Name')} />
                        </Col>
                        <Col colsize="50">
                            <FormLabel>{t('customer.ArticleGroup.Type')}</FormLabel>
                            <RadioWrapper role="group" aria-labelledby="my-radio-group">
                                <div>
                                    {_.map(articleGroupTypes, (agt, ix) => (
                                        <label key={ix}>
                                            <Field type="radio" name={`${path}.articleGroupType`} value={agt} />
                                            {agt === 'Default' ? t('customer.ArticleGroup.Other') : agt}
                                        </label>
                                    ))}
                                </div>
                                <ErrorMessage name={`${path}.articleGroupType`}>
                                    {(msg) => <ValidationErrorTxt>{msg}</ValidationErrorTxt>}
                                </ErrorMessage>
                            </RadioWrapper>
                        </Col>

                        <Col colsize="25">
                            <FormDatePicker
                                label={t('customer.StartDate')}
                                name={`${path}.startDate`}
                                selected={startDate}
                                onChange={(date) => {
                                    setStartDate(date);
                                    setFieldTouched(`${path}.startDate`, true);
                                    setFieldTouched(`${path}.endDate`, true); // setting both to touched since they are connected
                                }}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                dateFormat="dd-MM-yyyy"
                                calendarStartDay={1}
                            />
                        </Col>
                        <Col colsize="25">
                            <FormDatePicker
                                label={t('customer.EndDate')}
                                name={`${path}.endDate`}
                                selected={endDate}
                                onChange={(date) => {
                                    setEndDate(date);
                                    setFieldTouched(`${path}.startDate`, true);
                                    setFieldTouched(`${path}.endDate`, true); // setting both to touched since they are connected
                                }}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                dateFormat="dd-MM-yyyy"
                                calendarStartDay={1}
                            />
                        </Col>

                        <Col colsize="25">
                            <FormLabel>{t('customer.ArticleGroup.FixedQuantity')}</FormLabel>
                            <FormInput name={`${path}.fixedQuantity`} placeholder="0" />
                        </Col>

                        <Col colsize="25">
                            <FormLabel>{t('customer.ArticleGroup.VariableQuantity')}</FormLabel>
                            <FormInput name={`${path}.variableQuantity`} placeholder="0" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.ArticleGroup.PricePerPalletFixed')}</FormLabel>
                            <FormInput name={`${path}.pricePerPalletFixed`} prefixIcon="euro" placeholder="0.00" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.ArticleGroup.PricePerPalletVariable')}</FormLabel>
                            <FormInput name={`${path}.pricePerPalletVariable`} prefixIcon="euro" placeholder="0.00" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.ArticleGroup.PricePerPalletHandlingIn')}</FormLabel>
                            <FormInput name={`${path}.pricePerPalletHandlingIn`} prefixIcon="euro" placeholder="0.00" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.ArticleGroup.PricePerPalletHandlingOut')}</FormLabel>
                            <FormInput
                                name={`${path}.pricePerPalletHandlingOut`}
                                prefixIcon="euro"
                                placeholder="0.00"
                            />
                        </Col>
                    </Wrapper>
                </Accordion.Body>
            )}
        </Accordion>
    );
}

export default withTranslation()(AddArticleGroup);

const Wrapper = styled(Row)`
    align-items: baseline;
`;

const RadioWrapper = styled.div`
    height: 40px;
    > div {
        display: flex;
        align-items: center;
        height: 100%;
    }
`;
